import React, { useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { apply } from '../../features/ContainerQuery'
import useResizeObserver from '../../features/useResizeObserver'

/**
 * The DescriptionList component is our implementation of the
 * [HTML Description List element `<dl>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dl)
 * and is offered in different responsive layout variants.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-description-list.html
 *
 * @param {DescriptionListGroup[]} [children] - A collection of {@link DescriptionListGroup} items.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {boolean} [grid] - Render the Grid variant.
 * @param {boolean} [horizontal] - Render the Horizontal variant.
 * @param {boolean} [shortTerms] - Render the Short Terms variant.
 * @param {boolean} [split] - Render the Split variant (utilizes {@link useResizeObserver}).
 * @param {boolean} [table] - Render the Table variant.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The DescriptionList element.
 * @constructor
 */
export function DescriptionList({
  children,
  className,
  grid,
  horizontal,
  shortTerms,
  split,
  table,
  testId,
  otherProps,
}) {
  const ref = useRef(null)
  const applyQC = useCallback(apply, [apply])
  /**
   * Apply `onResizeHandler` handler conditionally to only use it
   * when in {@link split} mode.
   */
  useResizeObserver({
    ref,
    onResizeHandler: split ? applyQC : null,
  })

  const classNameList = classNames(
    'c-description-list',
    {
      // Convenience props from Bronson variants.
      'c-description-list--grid': grid,
      'c-description-list--horizontal': horizontal,
      'c-description-list--terms-short': shortTerms,
      'c-description-list--split': split,
      'c-description-list--table': table,
    },
    className
  ).trim()

  return (
    <dl ref={ref} className={classNameList} data-testid={testId} {...otherProps}>
      {children}
    </dl>
  )
}

DescriptionList.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node]), // Bronson template: 'items'.
  className: PropTypes.string, // Additional CSS classes on the component element. Bronson template: 'modifiers'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
  /* Convenience props. */
  grid: PropTypes.bool, // Convenience prop for `.c-description-list--grid`. Bronson template: 'modifiers'.
  horizontal: PropTypes.bool, // Convenience prop for `.c-description-list--horizontal`. Bronson template: 'modifiers'.
  shortTerms: PropTypes.bool, // Convenience prop for `.c-description-list--terms-short`. Bronson template: 'modifiers'.
  split: PropTypes.bool, // Convenience prop for `.c-description-list--split`. Bronson template: 'modifiers'.
  table: PropTypes.bool, // Convenience prop for `.c-description-list--table`. Bronson template: 'modifiers'.
}

/**
 * Internal DescriptionListGroup container.
 * @internal
 *
 * @param {DescriptionListDetail[]} [children] - A collection of {@link DescriptionListDetail} items.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {string} [id] - Unique identifier to pass.
 * @param {string} [termClassName] - Additional CSS classes on the `<dt>` element.
 * @param {string|React.ReactNode|React.ReactNodeArray} [termText] - The term label.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The DescriptionListGroup element.
 * @constructor
 */
function DescriptionListGroup({ children, className, id, termClassName, termText, testId, otherProps }) {
  const classNameList = classNames('c-description-list__group', className).trim()
  const dtClassNameList = classNames('c-description-list__term', termClassName).trim()

  return (
    <div className={classNameList} data-testid={testId} {...otherProps}>
      <dt className={dtClassNameList} id={id}>
        {termText}
      </dt>
      {children}
    </div>
  )
}

DescriptionListGroup.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node]), // Bronson template: 'details'.
  className: PropTypes.string, // Additional CSS classes on the component element.
  id: PropTypes.string, // Bronson template: 'id'.
  termClassName: PropTypes.string, // Bronson template: 'term.class'.
  termText: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.node, PropTypes.array]), // Bronson template: 'term.text'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}

DescriptionListGroup.displayName = 'DescriptionList.Group'
DescriptionList.Group = DescriptionListGroup

/**
 * Internal DescriptionListDetail container.
 * @internal
 *
 * @param {React.ReactNode|Button|InlineEditable} [action] - Additional action element of type {@link Button} or {@link InlineEditable}.
 * @param {string|React.ReactNode|React.ReactNodeArray} [children] - The content to display inside the DescriptionListDetail component.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @returns {JSX.Element} - The DescriptionListDetail element.
 * @constructor
 */
function DescriptionListDetail({ action, children, className }) {
  const ddClassNameList = classNames('c-description-list__detail', className).trim()

  return (
    <dd className={ddClassNameList}>
      {children && <span className="c-description-list__text">{children}</span>}
      {action && <div className="c-description-list__action">{action}</div>}
    </dd>
  )
}

DescriptionListDetail.propTypes = {
  action: PropTypes.oneOfType([PropTypes.element, PropTypes.node]), // Bronson template: 'detail.inline-editable' and 'detail.button'.
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.node, PropTypes.array]), // Bronson template: 'detail.text'.
  className: PropTypes.string, // Additional CSS classes on the component element. Bronson template: 'detail.class'.
}

DescriptionListDetail.displayName = 'DescriptionList.Detail'
DescriptionList.Detail = DescriptionListDetail
